import type { $Values } from 'utility-types'

import i18n from 'modules/core/lib/i18n'

import { Trigger } from 'modules/automations/store/models/Trigger'

const Categories = {
  application: 'application',
  assignee: 'assignee',
  author: 'author',
  contract: 'contract',
  cost_center: 'cost_center',
  candidate: 'candidate',
  changes: 'changes',
  company: 'company',
  customFields: 'customFields',
  document: 'document',
  employee: 'employee',
  hiring: 'hiring',
  leave: 'leave',
  location: 'location',
  team: 'team',
  manager: 'manager',
}
type AllowedCategories = $Values<typeof Categories>

export interface VariableItem {
  category: AllowedCategories
  label: () => string
  payloadVariable: string
  isCustomTag?: boolean
}

export interface Category {
  key: AllowedCategories
  label: () => string
}

// TODO (Nelson): This whole file is painful (600 LOC just declaring variables).
// We should implement some kind of autodiscovery, specially when we fix the payloads (also painful).
const ChangesVariables: VariableItem[] = [
  {
    category: Categories.changes,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.updates'),
    payloadVariable: 'updates',
    isCustomTag: true,
  },
]

const AuthorVariables: VariableItem[] = [
  {
    category: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.first_name'),
    payloadVariable: 'author_first_name',
  },
  {
    category: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.last_name'),
    payloadVariable: 'author_last_name',
  },
]

const DocumentVariables: VariableItem[] = [
  {
    category: Categories.document,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.type'),
    payloadVariable: 'document.type',
  },
  {
    category: Categories.document,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.folder_name'),
    payloadVariable: 'folder_name',
  },
  {
    category: Categories.document,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.folder_type'),
    payloadVariable: 'folder_type',
  },
]

const AssigneeVariables: VariableItem[] = [
  {
    category: Categories.assignee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.first_name'),
    payloadVariable: 'first_name',
  },
  {
    category: Categories.assignee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.last_name'),
    payloadVariable: 'last_name',
  },
]

const CompanyVariables: VariableItem[] = [
  {
    category: Categories.company,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.name'),
    payloadVariable: 'company.name',
  },
  {
    category: Categories.company,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.legal_name'),
    payloadVariable: 'company.legal_name',
  },
]

const EmployeeCommonVariables: VariableItem[] = [
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.first_name'),
    payloadVariable: 'first_name',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.last_name'),
    payloadVariable: 'last_name',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('common.email'),
    payloadVariable: 'email',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.birthday'),
    payloadVariable: 'birthday',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.age'),
    payloadVariable: 'age',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.gender'),
    payloadVariable: 'employee.gender',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.nationality'),
    payloadVariable: 'employee.nationality',
  },
  {
    category: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.employee_group_name'),
    payloadVariable: 'employee_group.name',
  },
]

const EmployeeTerminatedVariables: VariableItem[] = [
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.terminated_on'),
    payloadVariable: 'employee.terminated_on',
  },
  {
    category: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.termination_reason'),
    payloadVariable: 'employee.termination_reason',
  },
  {
    category: Categories.employee,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.termination_type'),
    payloadVariable: 'termination_type',
  },
]

const ManagerVariables: VariableItem[] = [
  {
    category: Categories.manager,
    label: () => i18n.t('common.name'),
    payloadVariable: 'manager_name',
  },
  {
    category: Categories.manager,
    label: () => i18n.t('common.email'),
    payloadVariable: 'manager_email',
  },
]

const LeaveVariables: VariableItem[] = [
  {
    category: Categories.leave,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.type'),
    payloadVariable: 'leave_type',
  },
  {
    category: Categories.leave,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.description'),
    payloadVariable: 'leave.description',
  },
  {
    category: Categories.leave,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.start_on'),
    payloadVariable: 'leave.start_on',
  },
  {
    category: Categories.leave,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.finish_on'),
    payloadVariable: 'leave.finish_on',
  },
  {
    category: Categories.leave,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.reason'),
    payloadVariable: 'leave.reason',
  },
]

const TeamVariables: VariableItem[] = [
  {
    category: Categories.team,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.team_name'),
    payloadVariable: 'team.name',
  },
]

const LocationVariables: VariableItem[] = [
  {
    category: Categories.location,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.location_name'),
    payloadVariable: 'location.name',
  },
  {
    category: Categories.location,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.location_country'),
    payloadVariable: 'location.country',
  },
]

const CostCenterVariables: VariableItem[] = [
  {
    category: Categories.cost_center,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.cost_center_name'),
    payloadVariable: 'cost_center.name',
  },
]

const DocumentCategories: Category[] = [
  {
    key: Categories.document,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.document'),
  },
  {
    key: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.author'),
  },
  {
    key: Categories.assignee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.assignee'),
  },
  {
    key: Categories.company,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.company'),
  },
  {
    key: Categories.customFields,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.custom_fields'),
  },
  {
    key: Categories.changes,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.changes'),
  },
]

const CostCenterCategories: Category[] = [
  {
    key: Categories.cost_center,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.cost_center'),
  },
  {
    key: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.employee'),
  },
]

const EmployeeCategories: Category[] = [
  {
    key: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.author'),
  },
  {
    key: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.employee'),
  },
  {
    key: Categories.manager,
    label: () => i18n.t('common.manager'),
  },
  {
    key: Categories.customFields,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.custom_fields'),
  },
  {
    key: Categories.changes,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.changes'),
  },
  {
    key: Categories.location,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.location'),
  },
  {
    key: Categories.team,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.team'),
  },
  {
    key: Categories.cost_center,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.cost_center'),
  },
]

const CustomFieldsCategories: Category[] = [
  {
    key: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.author'),
  },
  {
    key: Categories.customFields,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.custom_fields'),
  },
  {
    key: Categories.changes,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.changes'),
  },
]

const LeaveCategories: Category[] = [
  {
    key: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.author'),
  },
  {
    key: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.employee'),
  },
  {
    key: Categories.leave,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.leave'),
  },
  {
    key: Categories.customFields,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.custom_fields'),
  },
  {
    key: Categories.changes,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.changes'),
  },
]

const ApplicationCategories: Category[] = [
  {
    key: Categories.application,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.job_offer'),
  },
  {
    key: Categories.candidate,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.candidate'),
  },
  {
    key: Categories.company,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.company'),
  },
]

const ApplicationVariables: VariableItem[] = [
  {
    category: Categories.candidate,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.first_name'),
    payloadVariable: 'candidate.first_name',
  },
  {
    category: Categories.candidate,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.last_name'),
    payloadVariable: 'candidate.last_name',
  },
  {
    category: Categories.company,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.name'),
    payloadVariable: 'company.name',
  },
  {
    category: Categories.application,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.job_title'),
    payloadVariable: 'job_title',
  },
]

const ContractCategories: Category[] = [
  {
    key: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.author'),
  },
  {
    key: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.employee'),
  },
  {
    key: Categories.changes,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.changes'),
  },
]

const SchemaGenericCategories: Category[] = [
  {
    key: Categories.author,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.author'),
  },
  {
    key: Categories.employee,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.employee'),
  },
  {
    key: Categories.changes,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.changes'),
  },
  {
    key: Categories.location,
    label: () =>
      i18n.t('automations.actions.send_custom_notification.variables.categories.location'),
  },
  {
    key: Categories.team,
    label: () => i18n.t('automations.actions.send_custom_notification.variables.categories.team'),
  },
]

const costCenterPayloadVariables = (): VariableItem[] => [
  ...EmployeeCommonVariables,
  ...CostCenterVariables,
]

const documentPayloadVariables = (): VariableItem[] => {
  return [
    ...ChangesVariables,
    ...AuthorVariables,
    ...DocumentVariables,
    ...AssigneeVariables,
    ...CompanyVariables,
  ]
}

const employeeCommonPayloadVariables = (): VariableItem[] => [
  ...ChangesVariables,
  ...AuthorVariables,
  ...EmployeeCommonVariables,
  ...TeamVariables,
  ...LocationVariables,
  ...CostCenterVariables,
]

const employeeTerminatedPayloadVariables = (): VariableItem[] => [
  ...ChangesVariables,
  ...AuthorVariables,
  ...EmployeeCommonVariables,
  ...EmployeeTerminatedVariables,
  ...ManagerVariables,
  ...TeamVariables,
  ...LocationVariables,
]

const leavePayloadVariables = (): VariableItem[] => [
  ...ChangesVariables,
  ...AuthorVariables,
  ...EmployeeCommonVariables,
  ...LeaveVariables,
]

const schemaGrossSalaryVariables = (): VariableItem[] => [
  ...AuthorVariables,
  ...EmployeeCommonVariables,
]

const contractPayloadVariables = (): VariableItem[] => [
  ...AuthorVariables,
  ...ChangesVariables,
  ...EmployeeCommonVariables,
]

const customFieldsVariables = (): VariableItem[] => [
  ...ChangesVariables,
  ...AuthorVariables,
  ...EmployeeCommonVariables,
  ...DocumentVariables,
]

const schemaGenericVariables = (): VariableItem[] => [
  ...ChangesVariables,
  ...AuthorVariables,
  ...EmployeeCommonVariables,
  ...TeamVariables,
  ...LocationVariables,
]

const applicationPayloadVariables = () => [...ApplicationVariables]

export const variablesList = (trigger: Trigger): VariableItem[] => {
  // Time based variables
  if (trigger.isBeforeAfter) {
    return employeeCommonPayloadVariables()
  }

  if (trigger.isCustomSchemaTrigger && !trigger.isDeprecated) {
    return schemaGenericVariables()
  }

  switch (trigger.attributes.slug) {
    case Trigger.Slugs.documentCreated:
    case Trigger.Slugs.documentUpdated:
    case Trigger.Slugs.documentMadePublic:
      return documentPayloadVariables()

    case Trigger.Slugs.employeeCreated:
    case Trigger.Slugs.employeeUpdated:
      return employeeCommonPayloadVariables()

    case Trigger.Slugs.costCenterMembershipCreated:
      return costCenterPayloadVariables()

    case Trigger.Slugs.employeeTerminated:
      return employeeTerminatedPayloadVariables()

    case Trigger.Slugs.leaveCreated:
    case Trigger.Slugs.leaveUpdated:
    case Trigger.Slugs.leaveRejected:
    case Trigger.Slugs.leaveApproved:
      return leavePayloadVariables()

    case Trigger.Slugs.applicationCreated:
    case Trigger.Slugs.applicationDisqualified:
      return applicationPayloadVariables()

    case Trigger.Slugs.contractVersionUpdated:
      return contractPayloadVariables()

    case Trigger.Slugs.schemaGrossSalary:
      return schemaGrossSalaryVariables()

    case Trigger.Slugs.customFieldsValueUpdated:
      return customFieldsVariables()

    default:
      return []
  }
}

export const variablesCategoriesList = (trigger: Trigger): Category[] => {
  // Time based variables
  if (trigger.isRepeat || trigger.isBeforeAfter) {
    return EmployeeCategories
  }

  if (trigger.isCustomSchemaTrigger && !trigger.isDeprecated) {
    return SchemaGenericCategories
  }

  switch (trigger.attributes.slug) {
    case Trigger.Slugs.documentCreated:
    case Trigger.Slugs.documentUpdated:
    case Trigger.Slugs.documentMadePublic:
      return DocumentCategories

    case Trigger.Slugs.employeeCreated:
    case Trigger.Slugs.employeeTerminated:
    case Trigger.Slugs.employeeUpdated:
    case Trigger.Slugs.schemaGrossSalary:
      return EmployeeCategories

    case Trigger.Slugs.leaveCreated:
    case Trigger.Slugs.leaveUpdated:
    case Trigger.Slugs.leaveRejected:
    case Trigger.Slugs.leaveApproved:
      return LeaveCategories

    case Trigger.Slugs.applicationCreated:
    case Trigger.Slugs.applicationDisqualified:
      return ApplicationCategories

    case Trigger.Slugs.contractVersionUpdated:
      return ContractCategories

    case Trigger.Slugs.customFieldsValueUpdated:
      return CustomFieldsCategories

    case Trigger.Slugs.costCenterMembershipCreated:
      return CostCenterCategories

    default:
      return []
  }
}
